import { Component, Event, type EventEmitter, h, Prop } from '@stencil/core';
import { v4 as uuid } from 'uuid';

export type BaywaInputType = 'text' | 'password';
@Component({
  tag: 'baywa-input',
  shadow: false,
})
export class BaywaInput {
  /**
   * The corresponding input label
   */
  @Prop() label: string;
  /**
   * The input id
   */
  @Prop({ attribute: 'id' }) inputId: string = uuid();
  /**
   * The name of the input field
   */
  @Prop() name: string;
  /**
   * The native input field type
   */
  @Prop() type: BaywaInputType = 'text';
  /**
   * The placeholder
   */
  @Prop() placeholder: string;
  /**
   * Sets the input in read-only mode
   */
  @Prop() readonly: boolean = false;
  /**
   * The initial value
   */
  @Prop() value: string;
  /**
   * Enables or disables browsers noAutocomplete / autofill functionality
   */
  @Prop() noAutocomplete: boolean = false;
  /**
   * Additional classes (unintentional solution in order to inject scope component classes from parent components)
   * Apparently, Stencil uses class to automatically inject component classes, so internally, it needs to direct to attribute class
   */
  @Prop({ attribute: 'class' }) classes: string = '';
  /**
   * Disables the input field
   */
  @Prop() disabled: boolean = false;
  /**
   * Truncates the visible text by adding an ellipsis (…)
   */
  @Prop() truncate: boolean = false;

  /**
   * The internal callback function to receive the inner element ref
   */
  @Event() inputRef: EventEmitter<HTMLInputElement>;
  /**
   * The input event for the input field ..emits the current value
   */
  @Event() inputText: EventEmitter<string>;
  /**
   * Fired when internal input field gets focus
   */
  @Event() inputFocus: EventEmitter<FocusEvent>;
  /**
   * Fired when internal input field loses focus
   */
  @Event() inputBlur: EventEmitter<FocusEvent>;
  /**
   *  Fired when internal input field was clicked
   */
  @Event() inputClick: EventEmitter<MouseEvent>;

  /**
   *  Fired when internal input field was clicked
   */
  @Event() inputMouseDown: EventEmitter<MouseEvent>;

  /**
   * The callback function in order to receive the inner input element ref
   */
  private inputElementRef: HTMLInputElement;

  private handleOnInput(): void {
    this.inputText.emit(this.inputElementRef.value);
  }

  componentDidRender(): void {
    this.inputRef.emit(this.inputElementRef);
  }

  private getOptionalStyles() {
    if (this.truncate) {
      return {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
      };
    }
  }

  render() {
    return (
      <div class={`baywa-input ${this.classes}`}>
        {this.label && <label htmlFor={this.inputId}>{this.label}</label>}
        <input
          autoComplete={this.noAutocomplete ? 'off' : 'on'}
          disabled={this.disabled}
          id={this.inputId}
          name={this.name}
          onClick={ev => this.inputClick.emit(ev)}
          onMouseDown={ev => this.inputMouseDown.emit(ev)}
          onFocus={ev => this.inputFocus.emit(ev)}
          onBlur={ev => this.inputBlur.emit(ev)}
          onInput={() => {
            this.handleOnInput();
          }}
          placeholder={this.placeholder}
          readOnly={this.readonly}
          ref={(ref: HTMLInputElement) => (this.inputElementRef = ref)}
          style={this.getOptionalStyles()}
          tabIndex={1}
          type={this.type}
          value={this.value ?? ''}
        />
        <slot />
      </div>
    );
  }
}
